import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { LoginApi } from '~/api/login-api';
import { AppEvents } from '~/app';
import { Notification } from '~/components/notification/notification';
import { TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import { LoginConstants } from '~/login/login-constants';
import { OktaLogin } from '~/login/okta-login';
import { getNativeTaskRunner } from '~/treeview/native-task-runner';
import { FetchUtils } from '~/utils/fetch-utils';
import { getItem, removeItem, setItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';

@autoinject
export class Authentication {
	constructor(
		private readonly i18n: I18N,
		private loginApi: LoginApi,
		private eventAggregator: EventAggregator,
	) {}

	public getLocalOrOktaAccessToken():
		| Promise<string | undefined>
		| string
		| undefined {
		if (OktaLogin.isLoggedIn()) {
			return OktaLogin.getAccessToken();
		} else {
			return LoginApi.getAccessToken();
		}
	}

	/**
	 * Asynchronously attempt to refresh the authentication token. The main functions of this method are the
	 * publishing of events and the updating of authorisation token information.
	 * The result of the attempt is published via an AppEvents.TokenRefreshed event. The data published with the event
	 * is a boolean that is true when the attempt succeeded.
	 * If the attempt fails due to an unrecoverable error, an AppEvents.Logout event is published.
	 * Note: there should be not more than one attempt at a time, so this does nothing when called while an attempt
	 * is ongoing.
	 * @param initiator Contains debug info that the server can log.
	 */
	public tryToRefreshToken(initiator: string = ''): void {
		const refreshingToken: boolean = this.isTokenRefreshing();
		const unloading: boolean = JSON.parse(
			getItem(LoginConstants.UNLOADING_PAGE),
		);
		if (refreshingToken || unloading) {
			Logger.warn('Refreshing token?', refreshingToken);
			Logger.warn('Unloading?', unloading);
			return;
		}

		this.setIsTokenRefreshing(true);
		const refreshToken = getItem(LoginConstants.REFRESH_TOKEN);
		if (!refreshToken) {
			// We will log out on this event
			this.eventAggregator.publish(AppEvents.TokenRefreshed, false);
			throw new Error('Refresh token is missing! It should not happen!');
		}

		Logger.log('Refreshing access token');
		this.loginApi
			.refreshAccessToken(refreshToken, initiator, {
				refreshing: refreshingToken,
				unloading,
			})
			.then((res) => {
				Logger.log('Refresh access token succeeded.');
				this.eventAggregator.publish(AppEvents.TokenRefreshed, true);
			})
			.catch((error) => {
				let authenticationSuccessful = false;
				const isTreeView =
					sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) ===
					'1';
				const nativeTaskRunner = getNativeTaskRunner();

				if (isTreeView && !!nativeTaskRunner) {
					Logger.log('Refresh access token failed.');
					Logger.log(
						'Try to refresh token through a silent TreeView login.',
					);
					authenticationSuccessful = nativeTaskRunner.authenticate();
					if (authenticationSuccessful) {
						Logger.log('TreeView login succeeded.');
					} else {
						Logger.log('TreeView login failed.');
					}
				}
				this.eventAggregator.publish(
					AppEvents.TokenRefreshed,
					authenticationSuccessful,
				);

				if (!authenticationSuccessful) {
					if (isTreeView && !nativeTaskRunner) {
						Notification.warning(
							this.i18n.tr(
								'report:native-task-runner-not-registered',
							),
						);
					}

					FetchUtils.logAndDisplayError(error);
					this.eventAggregator.publish(AppEvents.Logout);
				}
			});
	}

	/**
	 * Check login expireTime
	 * @returns {boolean} Is login expired?
	 */
	public isLoginExpired(): boolean {
		if (!this.isLoggedIn()) {
			return true;
		}
		// Okta tokens handle expiry separately to OAuth so Okta logins are not "expired" in the same way..
		if (OktaLogin.isLoggedIn()) {
			return false;
		}
		const expireTime = this.getExpireTime();
		if (!!expireTime) {
			const now = new Date();
			if (now >= expireTime) {
				Logger.log(
					'Login',
					'Token is expired. ' + expireTime + ' vs ' + now,
				);
				return true;
			}
		}
		return false;
	}

	/**
	 * Is the user logged in -- according to localStorage?
	 * @returns {boolean} Is user logged in?
	 */
	public isLoggedIn(): boolean {
		return (
			(!!LoginApi.getAccessToken() && !!this.getRefreshToken()) ||
			OktaLogin.isLoggedIn()
		);
	}

	/**
	 * Get refresh token
	 * @returns {string} Refresh token
	 */
	public getRefreshToken(): string | null {
		return getItem(LoginConstants.REFRESH_TOKEN);
	}

	/**
	 * Gets the expire time
	 * @returns {Date} Expire time
	 */
	public getExpireTime(): Date | null {
		const dateString = getItem(LoginConstants.EXPIRE_TIME);
		return dateString && new Date(dateString);
	}

	/**
	 * Returns true if request has been made to refresh the authentication token that has not been resolved.
	 */
	public isTokenRefreshing(): boolean {
		return !!JSON.parse(getItem(LoginConstants.REFRESHING_TOKEN));
	}

	public setIsTokenRefreshing(isTokenRefreshing: boolean) {
		if (isTokenRefreshing) {
			setItem(LoginConstants.REFRESHING_TOKEN, true);
		} else {
			removeItem(LoginConstants.REFRESHING_TOKEN);
		}
	}
}
